import type { useNavigate } from "@tanstack/react-router";

import type { NotificationInboxItem, NotificationLink } from "@recobra/schemas/notification";

export type NotificationNavigationTarget =
  | { kind: "external"; href: string }
  | { kind: "tasks"; search: { taskId: string } }
  | {
      kind: "case";
      params: { caseNumber: string };
      search?: { tab?: string; historyEntryId?: string };
    }
  | { kind: "client"; params: { id: string } }
  | { kind: "notifications" };

const resolveLinkTarget = (link: NotificationLink): NotificationNavigationTarget | null => {
  switch (link.type) {
    case "task":
      return { kind: "tasks", search: { taskId: link.taskId } };
    case "case": {
      if (link.caseNumber == null) return null;

      return {
        kind: "case",
        params: { caseNumber: String(link.caseNumber) },
        ...(link.tab || link.historyEntryId
          ? { search: { tab: link.tab, historyEntryId: link.historyEntryId } }
          : {}),
      };
    }
    case "client":
      return { kind: "client", params: { id: link.clientId } };
    case "conversation":
      return { kind: "external", href: `/conversations/${link.conversationId}` };
    case "url":
      return { kind: "external", href: link.href };
    case "notifications":
      return { kind: "notifications" };
  }
};

/**
 * `link` is the in-app destination and always wins, so navigation stays inside
 * the router; `action.href` is only consulted when it points off-site.
 */
export const resolveNotificationTarget = (
  item: NotificationInboxItem,
): NotificationNavigationTarget | null =>
  item.action?.href?.startsWith("http")
    ? { kind: "external", href: item.action.href }
    : resolveLinkTarget(item.link);

const caseDetailTabs = ["contacts", "documents", "overview", "timeline"] as const;

type CaseDetailTab = (typeof caseDetailTabs)[number];

const isCaseDetailTab = (tab: string): tab is CaseDetailTab =>
  (caseDetailTabs as readonly string[]).includes(tab);

export const navigateToNotificationTarget = (
  navigate: ReturnType<typeof useNavigate>,
  target: NotificationNavigationTarget,
  canViewAllTasks: boolean,
): void => {
  switch (target.kind) {
    case "external":
      window.location.assign(target.href);
      return;
    case "tasks":
      void navigate({
        to: canViewAllTasks ? "/tasks" : "/my-tasks",
        search: target.search,
      });
      return;
    case "case": {
      const tab = target.search?.tab;
      if (tab && isCaseDetailTab(tab)) {
        void navigate({
          to: "/cases/$caseNumber",
          params: target.params,
          search: { tab, historyEntryId: target.search?.historyEntryId },
        });
        return;
      }

      void navigate({ to: "/cases/$caseNumber", params: target.params });
      return;
    }
    case "client":
      void navigate({ to: "/clients/$id", params: target.params });
      return;
    case "notifications":
      void navigate({ to: "/notifications" });
      return;
  }
};
